import { BooleanField, ModelField, ModelGroup, NumberField, StringField } from '@mng/model';
import {
  createFzKontoBezeichnungField,
  createFzKontoDienststelleRequiredField,
  createFzKontoExklusivAbberNrField,
  createFzKontoGiltBisField,
  createFzKontoGiltVonField,
  createFzKontoHinweisField,
  createFzKontoKontoField,
  createFzKontoKontoNameField,
  createFzKontoPersonalExplanationField,
  createFzKontoPersonalNummer,
  createFzKontoPersonalNummerRegexField,
  createFzKontoVerrechnungsartField,
  createFzPersNrErforderlichField
} from '../allgemein/fz-konto-base';
import { gvVersicherungsbeginnValidation } from './gvangebot.validation';
import {
  createGvAusgefuehrteGeschaeftsfaelleModelGroup,
  createGvErlaubteGeschaeftsfaelleModelGroup
} from './gvgeschaeftsfall.model';
import { gvTarifDataValidation, gvTarifValidation } from './gvtarif.validation';

export function createGvTarifDataModelGroup(): ModelGroup {
  return new ModelGroup('gvTarifData')
    .withValidation(gvTarifDataValidation)
    .addArrayGroupObject(createGvTarifeModelGroup());
}

function createGvTarifInfoModelGroup(id: string, interfaceName: string): ModelGroup {
  return new ModelGroup(id).withInterfaceName(interfaceName).addItems(
    new StringField('tarifKbez'),
    new StringField('tarifSchl'),
    new StringField('tarifBez'),
    new StringField('tarifText'),
    new StringField('hierarchie'), // für Druck notwendig (aus KVTARIFE.DBF)
    new BooleanField('firmenTarif'),
    new StringField('gesperrtAb'),
    new StringField('krankenAnstalten'),
    new StringField('kostenDeckungBis'),
    new StringField('kostenDeckungVon'),
    new StringField('konvGruppe'),
    new StringField('tarifBausteinGruppe'),
    new StringField('tarifBundeslandNichtDeckungsBundesland'),
    new BooleanField('vkGesperrt'),
    new BooleanField('oeTarif'),
    new BooleanField('inGkktari'),
    new BooleanField('konvNurInGD'),
    new StringField('pk'),
    new StringField('tarifGruppe'),
    new StringField('bundesland'),
    new StringField('tarifZweig'),
    new StringField('sozVersAnstalten').asArray(),
    new StringField('sozVersTarifGrp').asArray(),
    new StringField('tarifSelektionsGruppe1').asArray(),
    new StringField('tarifHauptGruppen').asArray(),
    new StringField('tarifKlasseRikoPr'),
    new StringField('tarifZusatzGruppen').asArray()
  );
}

function createGvTarifZusaetzeModelGroup(id: string, interfaceName: string): ModelGroup {
  return new ModelGroup(id)
    .withInterfaceName(interfaceName)
    .addItems(
      new StringField('tarifSchl'),
      new StringField('kurzBezeichnung'),
      new StringField('langBezeichnung'),
      new NumberField('tarifZusatzPraemie')
    );
}

export function createFzKontoFields(): ModelField[] {
  return [
    createFzKontoKontoField(),
    createFzKontoKontoNameField(),
    createFzKontoExklusivAbberNrField().withRemoveDataWhenNotRelevant().asArray(),
    createFzKontoVerrechnungsartField(),
    createFzKontoBezeichnungField(),
    createFzKontoHinweisField(),
    createFzKontoGiltVonField(),
    createFzKontoGiltBisField(),
    createFzKontoPersonalNummer(),
    createFzKontoPersonalNummerRegexField(),
    createFzKontoPersonalExplanationField(),
    createFzKontoDienststelleRequiredField(),
    createFzPersNrErforderlichField()
  ];
}

export function createFzKontoModelGroup(): ModelGroup {
  const fzKonto: ModelGroup = new ModelGroup('fzKonto', 'FZ Konto')
    .withInterfaceName('GvFzKonto')
    .addItems(...createFzKontoFields(), new NumberField('rabatt', 'Rabatt'));
  return fzKonto;
}

function createGvBerechnungMessageModelGroup(id = 'berechnungMessage'): ModelGroup {
  const berechnungMessage: ModelGroup = new ModelGroup(id, 'Message')
    .withInterfaceName('GvBerechnungMessage')
    .addItems(
      new StringField('txt'),
      new StringField('status'),
      new StringField('statusDescription'),
      new StringField('field'),
      new StringField('fieldDescription'),
      new StringField('code'),
      new BooleanField('fromAktuarMed')
    );
  return berechnungMessage;
}

function createGvBerechnungMessagesGroup(): ModelGroup {
  const berechnungMessages: ModelGroup = new ModelGroup('berechnungMessages', 'Messages nach Berechnung')
    .withInterfaceName('GvBerechnungMessages')
    .addItems(
      createGvBerechnungMessageModelGroup().asArray(),
      createGvTarifInfoModelGroup('tarifNichtkombinierbarMit', 'GvTarifNichtKombinierbarMit').asArray()
    );
  return berechnungMessages;
}

export function createGvTarifeModelGroup(id = 'tarife'): ModelGroup {
  const gvTarifZusaetzeModelGroup: ModelGroup = createGvTarifZusaetzeModelGroup('zusaetze', 'GvTarifZusatz').asArray();

  const gvTarifInfoModelGroup: ModelGroup = createGvTarifInfoModelGroup('tarifInfo', 'GvTarifInfo');

  const gvRabatteModelGroup: ModelGroup = new ModelGroup('rabatte', 'GvRabatt')
    .withInterfaceName('GvRabatt')
    .addItems(
      new StringField('zuschlagArt', 'Zuschlag Art'),
      new NumberField('prozent', 'Prozent'),
      new StringField('rabattArt', 'Rabatt Art'),
      new BooleanField('verrechnungskonto', 'Verrechnungskonto'),
      new NumberField('code', 'Code')
    )
    .asArray();

  const gvBausteineModelGroup: ModelGroup = new ModelGroup('bausteine', 'GvBaustein')
    .withInterfaceName('GvBaustein')
    .addItems(
      new StringField('kbez', 'Kurzbezeichnung'),
      new BooleanField('druck', 'Relevant für Druck'),
      new NumberField('praemie', 'Baustein-Prämie')
    )
    .asArray();

  const gvKlauselnModelGroup: ModelGroup = new ModelGroup('klauselDetails', 'GvKlausel')
    .withInterfaceName('GvKlausel')
    .addItems(new StringField('klauselNummer', 'Klausel ID'), new StringField('klauselBis', 'Gueltigkeit bis'))
    .asArray();

  const gvSonderRabattModelGroup: ModelGroup = new ModelGroup('sonderRabatte', 'GvSonderRabatt')
    .withInterfaceName('GvSonderRabatt')
    .addItems(new StringField('zuschlagArt', 'Zuschlagart'), new NumberField('prozent', 'Prozent'))
    .asArray();

  return new ModelGroup(id, 'GvTarif')
    .withInterfaceName('GvTarif')
    .withValidation(gvTarifValidation)
    .addItems(
      new NumberField('risikoNr').withReadOnly(),
      new StringField('versPraemArt').withReadOnly(),
      new StringField('zahlweise').withReadOnly(),
      new StringField('hauptgruppen').asArray().withReadOnly(),
      new BooleanField('ausgewaehlt'),
      new BooleanField('ehepaarRabatt'),
      new BooleanField('gkKontoGvau'),
      new BooleanField('zukuenftigesRisiko'),
      new BooleanField('verbleibInAlterTarifserie').withReadOnly(),
      new NumberField('praemie'),
      new NumberField('faktor'),
      new NumberField('risikoProzent'),
      new StringField('versicherungsBeginn').withValidation(gvVersicherungsbeginnValidation),
      new StringField('echtBeginn'),
      new StringField('techBeginn'),
      new NumberField('konvertierungsBasisRisikoNr'),
      new StringField('vbText', 'Hinweis zum Versicherungsbeginn').withReadOnly(),
      new StringField('risikoTyp', 'Status der Bearbeitung des Tarifs').withReadOnly(),
      new NumberField('geschaeftsfall').withReadOnly(),
      new StringField('praemienbefreiung').withReadOnly(),
      new StringField('konvBasisTarifSchl').withReadOnly(),
      new BooleanField('fromIddBedarf').withReadOnly(),
      new StringField('avbCode'),
      new NumberField('praemieErwachsen'),
      new NumberField('alterErwachsen'),
      new NumberField('gvAlter'),
      new BooleanField('leistungsausschluss'),
      gvTarifInfoModelGroup,
      gvTarifZusaetzeModelGroup,
      gvSonderRabattModelGroup,
      gvRabatteModelGroup,
      gvKlauselnModelGroup,
      new StringField('versBed', 'Versicherungsbedingungs ID').withReadOnly().asArray(),
      createFzKontoModelGroup(),
      createGvBerechnungMessagesGroup(),
      gvBausteineModelGroup,
      new StringField('ablauf').withReadOnly(),
      new StringField('tarifKlasse').withReadOnly()
    )
    .addArrayGroupObject(
      createGvErlaubteGeschaeftsfaelleModelGroup().withImportDef({
        localImport: true
      })
    )
    .addArrayGroupObject(
      createGvAusgefuehrteGeschaeftsfaelleModelGroup().withImportDef({
        localImport: true
      })
    );
}
